import React, { useCallback, useEffect, useRef, useState } from 'react';
import { withStyles } from '@mui/styles';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const SCROLL_STEP = 280;

const styles = () => ({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  track: {
    flex: 1,
    minWidth: 0,
    overflowX: 'auto',
    scrollbarWidth: 'thin',
    scrollSnapType: 'x mandatory',
    WebkitOverflowScrolling: 'touch',
  },
  arrow: {
    flexShrink: 0,
    border: '1px solid #d1d5db',
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.12)',
    '&:disabled': {
      opacity: 0.35,
    },
  },
});

const HorizontalScrollRow = ({ classes, className = '', contentClassName = '', children }) => {
  const trackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) {
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) {
      return undefined;
    }
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ResizeObserverClass =
      typeof window !== 'undefined' ? window.ResizeObserver : null;
    const resizeObserver = ResizeObserverClass
      ? new ResizeObserverClass(updateScrollState)
      : null;
    if (resizeObserver) {
      resizeObserver.observe(el);
    }
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, children]);

  const scrollBy = (direction) => {
    const el = trackRef.current;
    if (!el) {
      return;
    }
    el.scrollBy({ left: direction * SCROLL_STEP, behavior: 'smooth' });
  };

  return (
    <div className={`${classes.wrapper} ${className}`.trim()}>
      <IconButton
        className={classes.arrow}
        size="small"
        onClick={() => scrollBy(-1)}
        disabled={!canScrollLeft}
        aria-label="Scroll left"
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <div
        ref={trackRef}
        className={`${classes.track} ${contentClassName}`.trim()}
      >
        {children}
      </div>
      <IconButton
        className={classes.arrow}
        size="small"
        onClick={() => scrollBy(1)}
        disabled={!canScrollRight}
        aria-label="Scroll right"
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

export default withStyles(styles)(HorizontalScrollRow);



// WEBPACK FOOTER //
// ./src/components/billing/HorizontalScrollRow.js